/* eslint-disable @next/next/no-img-element */
"use client";
import { Icon } from "@components/blocks/icon/Icon";
import { Button } from "@components/ui/button";
import { useRoutes } from "@lib/utils/hooks/useRoutes";
import { MARKER_POPUP_OBJECT } from "@repo/map-helpers";
import { FacilitySummaryResponse } from "@repo/shared-types";
import { ArrowRight, X } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { SyntheticEvent } from "react";
import { Layer, Popup as MapboxPopup, PopupEvent, Source } from "react-map-gl";

import { LINE_LAYOUT, NAVIGATION_LINE_PAINT } from "../consts";
import { useDistance } from "./markerPopup.hooks";

export interface IMarkerPopup {
  categoryId: keyof typeof MARKER_POPUP_OBJECT;
  id: string;
  latitude: number;
  longitude: number;
  onClose:
    | ((e: PopupEvent | SyntheticEvent<HTMLButtonElement>) => void)
    | undefined;
  facility: FacilitySummaryResponse;
}

export function MarkerPopup({
  id,
  latitude,
  longitude,
  onClose,
  categoryId,
  facility,
}: IMarkerPopup) {
  const [distance, coordinates] = useDistance({ latitude, longitude });
  const t = useTranslations("Map");
  const routes = useRoutes();

  return (
    <>
      <MapboxPopup
        offset={20}
        anchor="top"
        key={id}
        latitude={latitude}
        longitude={longitude}
        closeOnClick={false}
        onClose={onClose}
        className="max-w-unset"
        style={{
          maxWidth: "unset",
        }}
      >
        <div className="items-top relative flex h-[96px] flex-row justify-start gap-3 pr-8">
          {facility?.image ? (
            <span className="relative min-h-[96px] min-w-[96px]">
              <img
                alt={facility.image.altText || "place image"}
                src={facility.image.url}
                onError={undefined}
                className="h-[96px] w-[96px] rounded-xl object-cover"
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                <Icon
                  iconName={
                    MARKER_POPUP_OBJECT[categoryId] ?? "markerFallbackWhite"
                  }
                />
              </span>
            </span>
          ) : (
            <div className="grid h-[96px] min-h-[96px] w-[96px] min-w-[96px] items-center justify-center rounded-xl bg-[#DCE8CA]">
              <Icon iconName="logoMarker" />
            </div>
          )}
          <Button
            size="xs"
            className="absolute right-0 top-0 rounded-full"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="items-top flex max-w-[160px] flex-col">
            <p className="truncate text-base font-bold">{facility.name}</p>
            <p className="text-xs font-light">{facility?.address?.street}</p>
            <span className="mt-auto flex w-[195px] items-center">
              {distance && (
                <p className="text-xs font-light">
                  {distance} {t("DistanceIndicator")}
                </p>
              )}
              <Link
                href={routes.facility.route({ id: facility.id })}
                className="ml-auto flex h-6 w-6 items-center justify-center gap-4 text-balance rounded-full border border-accent text-sm font-medium text-accent ring-offset-background transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
              >
                <ArrowRight className="h-[10px] w-[10px]" />
              </Link>
            </span>
          </div>
        </div>
      </MapboxPopup>
      {coordinates && (
        <Source
          type="geojson"
          data={{
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: coordinates,
            },
          }}
        >
          <Layer
            type="line"
            layout={LINE_LAYOUT}
            paint={NAVIGATION_LINE_PAINT}
          />
        </Source>
      )}
    </>
  );
}
